import type { PartDefinition, MaterialInfo } from '@rogue/rackbuilder'
import { AdjustOnlyVisual } from '../../../constants'
import { getRackModel, UTILITY_BENCH, ADJ_BENCH_3 } from '../../../modelPaths'
import { RackOptions } from '../../../RackTypes'
import { AccessoryBuilder } from '../builders/accessoryBuilder'
import { MaterialBuilder } from '../builders/materialBuilder'

// Builds flat foot wall-mounted racks (RM/RML 390F)
export class FlatFootBuilder {
  protected line: RackOptions['line']
  protected material: MaterialInfo
  protected materialBuilder: MaterialBuilder
  protected accessoryBuilder: AccessoryBuilder

  constructor(protected config: RackOptions) {
    this.line = config.line
    this.materialBuilder = new MaterialBuilder(config)
    this.material = this.materialBuilder.createMainMaterial()
    this.accessoryBuilder = new AccessoryBuilder(config, this.material)
  }

  public build(): PartDefinition {
    return this.assembleRack()
  }

  protected assembleRack(): PartDefinition {
    const leftSide = this.createSide(true)
    const rightSide = this.createSide(false)

    leftSide.slots['other-side'] = rightSide
    leftSide.id = 'main'

    return leftSide
  }

  // Accessories, branding, and bench all attach to the left side
  // MARK: Build
  protected createSide(left: boolean): PartDefinition {
    const frontUpright = this.createFrontUpright()
    const backUpright = this.createBackUpright()
    const base = this.createBase(left, backUpright)

    // Connect structure
    frontUpright.slots.bottom = base
    frontUpright.slots['crossmember-top'] = this.createTopCrossmember(left)

    // Add accessories
    this.attachJCup(backUpright, left)
    this.attachSafeties(frontUpright, left)
    this.attachPullupBar(frontUpright, left)
    this.attachBackCrossmember(backUpright, left)
    this.attachBranding(frontUpright, backUpright, left)
    this.attachBench(backUpright, left)

    return frontUpright
  }

  protected createFrontUpright(): PartDefinition {
    return {
      mesh: this.line === 'RML'
        ? getRackModel('WM_RML_PARTS', 'RML stick')
        : getRackModel('WM_RM_PARTS', 'RM stick numbered'),
      slots     : {},
      materials : this.material,
    }
  }

  protected createBackUpright(): PartDefinition {
    return {
      mesh: this.line === 'RML'
        ? getRackModel('WM_RML_PARTS', 'RML stick')
        : getRackModel('WM_RM_PARTS', 'RM stick numbered'),
      slots     : {},
      anchor    : 'base',
      materials : this.material,
    }
  }

  protected createBase(left: boolean, backUpright: PartDefinition): PartDefinition {
    return {
      mesh  : getRackModel('SPECIALTY_PARTS', `${this.line} base`),
      slots : {
        upright: backUpright,
      },
      anchor         : 'upright',
      materials      : this.material,
      adjustments    : left ? [{ type: 'rotate', axis: 'Y', amount: 180 }] : [],
      adjustmentType : AdjustOnlyVisual,
    }
  }

  protected createTopCrossmember(left: boolean): PartDefinition {
    const isRM = this.line === 'RM'
    
    return {
      mesh: isRM
        ? getRackModel(`${this.line}_CROSSMEMBERS`, 'crossmember_top_2')
        : getRackModel('SPECIALTY_PARTS', 'rml 390f 3'),
      slots       : {},
      anchor      : left || !isRM ? 'front' : 'back',
      materials   : this.material,
      adjustments : left || !isRM ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }
  }

  protected attachJCup(backUpright: PartDefinition, left: boolean): void {
    const jcup = this.accessoryBuilder.createJCup(left)

    if (jcup) {
      backUpright.slots['jcup-high'] = jcup

      // Add Bar
      if (this.config.bar && left) {
        const bar = this.accessoryBuilder.createBar()

        jcup.slots.bar = bar
      }
    }
  }

  protected attachSafeties(frontUpright: PartDefinition, left: boolean): void {
    const safeties: PartDefinition = {
      mesh: this.line === 'RM' 
        ? getRackModel(`${this.line}_SAFETY`, `${this.config.safeties}-2`) 
        : getRackModel(`${this.line}_SAFETY`, `${this.config.safeties}-3`),
      slots       : {},
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
    }

    if (safeties) {
      frontUpright.slots['safety-high'] = safeties
    }
  }

  protected attachPullupBar(frontUpright: PartDefinition, left: boolean): void {
    if (!this.config.front || !left) return

    const pullupBar: PartDefinition = {
      mesh  : getRackModel(`${this.line}_PULLUP`, this.config.front),
      slots : {},
    }

    frontUpright.slots.top = pullupBar
  }

  protected attachBench(backUpright: PartDefinition, left: boolean): void {
    if (!this.config.bench || !left) return

    const bench: PartDefinition = {
      mesh   : this.config.bench === 'flat' ? UTILITY_BENCH : ADJ_BENCH_3,
      slots  : {},
      offset : this.config.backStabilizer ? { z: 65 } : { z: 45 },
    }

    backUpright.slots.bench = bench
  }

  protected attachBackCrossmember(backUpright: PartDefinition, left: boolean): void {
    if (!left) return

    const isRM = this.line === 'RM'

    const backCrossmember: PartDefinition = {
      mesh: isRM
        ? getRackModel(`${this.line}_CROSSMEMBERS`, 'crossmember_top_4')
        : getRackModel('SPECIALTY_PARTS', 'rml 390f 4'),
      slots       : {},
      anchor      : isRM ? 'snowman_bottom' : 'back',
      materials   : this.material,
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
    }

    backUpright.slots.top = backCrossmember
  }

  protected attachBranding(frontUpright: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    if (this.line === 'RM' && !left) {
      // RM: Branding plate on right front upright
      const brandingPlate: PartDefinition = {
        mesh      : getRackModel('RM_UPRIGHTS', 'Branding_Plate'),
        slots     : {},
        anchor    : 'top',
        materials : { hardware: { material: this.config.hardware } },
        offset    : { y: 1 },
      }
      frontUpright.slots.top = brandingPlate
    }

    if (this.line === 'RML') {
      // RML: Stickers on both sides
      const brandingSticker: PartDefinition = {
        mesh        : getRackModel('RML_UPRIGHTS', 'sticker'),
        slots       : {},
        adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      }

      if (!left) {
        frontUpright.slots['sticker-right'] = brandingSticker
      } else {
        backUpright.slots['sticker-left'] = brandingSticker
      }
    }
  }
}